import "@theme-toggles/react/css/Expand.css";
import { theme } from "antd";
import { blue } from "@ant-design/colors";
import esES from "antd/lib/locale/es_ES";
import { getDefaultThemeColors } from "../../utils/defaultThemeColors";
import { textColors } from "./tokens/colors";

/**
 * Estilos globales y configuración de tokens de diseño para toda la app.
 * Define el tema, colores, bordes, fuentes y overrides de componentes de Ant Design.
 * @param {object} state - Estado global de la app (incluye algorithm para dark/light)
 * @returns {object} Configuración para el ConfigProvider de Ant Design
 */

export const configProvider = (state) => {
  // Default colors if not provided in state
  // IMPORTANTE: Si no hay primaryColor en el estado, usar negro (#000000) en lugar de azul
  // para que respete la configuración de la sucursal
  const primaryColor = state.primaryColor || "#000000";
  // Debug mode: Solo loggear en desarrollo si es necesario
  // const isDev = process.env.NODE_ENV === 'development';

  // Dark mode adjustments
  const isDark = state.algorithm === "dark";
  const primaryColorDark = state.primaryColor || "#000000";

  // Cargar colores configurables desde estado o usar valores por defecto
  const defaultColors = getDefaultThemeColors();
  const roleColors = state.roleColors || defaultColors.roleColors;
  const semanticColors = state.semanticColors || defaultColors.semanticColors;
  const gradientColors = state.gradientColors || defaultColors.gradientColors;
  const statusColors = state.statusColors || defaultColors.statusColors;

  // Configuraciones dinámicas desde el estado
  const fontSize = state.fontSize || 14;
  const borderRadius =
    state.borderRadius !== undefined ? state.borderRadius : 6;
  const componentSize = state.componentSize || "middle";
  const motionEnabled = state.motion !== false; // Por defecto true
  const isCompact = state.compact || false;

  const textPrimary = isDark ? textColors.darkPrimary : textColors.primary;
  const textSecondary = isDark
    ? textColors.darkSecondary
    : textColors.secondary;
  const textTertiary = isDark ? textColors.darkTertiary : textColors.tertiary;
  const textQuaternary = isDark
    ? "rgba(255,255,255,0.35)"
    : textColors.disabled;

  // Calcular tamaños basados en fontSize
  const fontSizeLG = fontSize + 2;
  const fontSizeSM = fontSize - 2;
  const fontSizeXL = fontSize + 6;

  // Calcular radios basados en borderRadius
  const borderRadiusLG = borderRadius + 2;
  const borderRadiusSM = Math.max(0, borderRadius - 2);

  // Calcular espaciado (compacto o normal)
  const basePadding = isCompact ? 12 : 16;
  const baseMargin = isCompact ? 12 : 16;

  // Calcular control height basado en componentSize
  const controlHeights = {
    small: { base: 24, lg: 32, sm: 20 },
    middle: { base: 32, lg: 40, sm: 24 },
    large: { base: 40, lg: 48, sm: 32 },
  };
  const heights = controlHeights[componentSize] || controlHeights.middle;

  // Duración de animaciones (0 si están deshabilitadas)
  const motionDurationFast = motionEnabled ? "0.1s" : "0s";
  const motionDurationMid = motionEnabled ? "0.2s" : "0s";
  const motionDurationSlow = motionEnabled ? "0.3s" : "0s";

  return {
    locale: esES,
    theme: {
      algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: {
        // Colores principales
        colorPrimary: isDark ? primaryColorDark : primaryColor,
        // Colores semánticos configurables
        colorSuccess: semanticColors.success || "#52c41a",
        colorWarning: semanticColors.warning || "#faad14",
        colorError: semanticColors.error || "#ff4d4f",
        colorInfo:
          semanticColors.info || (isDark ? primaryColorDark : primaryColor),

        // Fondos y layouts centrados en negros/grises profundos (Premium Dark)
        colorBgLayout: isDark ? "#0a0c10" : "#F0F2F2",
        colorBgContainer: isDark ? "#121418" : "#ffffff",
        colorBgElevated: isDark ? "#1a1d23" : "#ffffff",
        colorBgSpotlight: isDark ? "#1e222a" : "#fafafa",

        // Bordes y radios (configurables)
        borderRadius: borderRadius,
        borderRadiusLG: borderRadiusLG,
        borderRadiusSM: borderRadiusSM,
        // borderColor: secondaryColor, // Comentado para usar el default de Ant Design y evitar bordes blancos excesivos
        borderColorSplit: isDark ? "#434343" : "#d9d9d9",

        // Tipografía (configurable)
        fontFamily: state.fontFamily
          ? state.fontFamily === "Inter"
            ? "'Inter', sans-serif"
            : state.fontFamily === "Roboto"
              ? "'Roboto', sans-serif"
              : state.fontFamily === "Open Sans"
                ? "'Open Sans', sans-serif"
                : state.fontFamily === "system-ui"
                  ? "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
                  : "Lato, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
          : "Lato, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        fontSize: fontSize,
        fontSizeLG: fontSizeLG,
        fontSizeSM: fontSizeSM,
        fontSizeXL: fontSizeXL,
        fontWeightStrong: 600,

        colorText: textPrimary,
        colorTextBase: textPrimary,
        colorTextSecondary: textSecondary,
        colorTextTertiary: textTertiary,
        colorTextQuaternary: textQuaternary,
        colorTextHeading: textPrimary,

        // Espaciado (configurable - compacto o normal)
        padding: basePadding,
        paddingLG: basePadding + 8,
        paddingSM: basePadding - 4,
        paddingXS: basePadding - 8,
        margin: baseMargin,
        marginLG: baseMargin + 8,
        marginSM: baseMargin - 4,
        marginXS: baseMargin - 8,

        // Sombras mejoradas (futuristas con múltiples capas)
        boxShadow: isDark
          ? "0 2px 8px rgba(0, 0, 0, 0.45)"
          : "0 2px 8px rgba(0, 0, 0, 0.15)",
        boxShadowSecondary: isDark
          ? "0 4px 16px rgba(0, 0, 0, 0.5)"
          : "0 4px 16px rgba(0, 0, 0, 0.12)",
        // Sombras adicionales para efectos futuristas
        boxShadowTertiary: isDark
          ? "0 8px 24px rgba(0, 0, 0, 0.55)"
          : "0 8px 24px rgba(0, 0, 0, 0.15)",
        // Sombra para glassmorphism
        boxShadowGlass: isDark
          ? "0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
          : "0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.8)",

        // Transiciones (configurables - pueden deshabilitarse)
        motionDurationFast: motionDurationFast,
        motionDurationMid: motionDurationMid,
        motionDurationSlow: motionDurationSlow,

        // Control height (configurable según componentSize)
        controlHeight: heights.base,
        controlHeightLG: heights.lg,
        controlHeightSM: heights.sm,

        // Tokens adicionales para colores configurables (accesibles desde useThemeTokens)
        // Estos se pasan a través del estado, no directamente en tokens de Ant Design
        // pero los exponemos aquí para referencia
        roleColors: roleColors,
        semanticColors: semanticColors,
        gradientColors: gradientColors,
        statusColors: statusColors,
      },
      components: {
        Modal: {},
        Image: {
          motionDurationSlow: "0.1s",
        },
        Statistic: {
          colorTextHeading: isDark ? "white" : "black",
          colorText: isDark ? "white" : "black",
          colorTitle: isDark ? "white" : "black",
          colorNumber: isDark ? "white" : "black",
          colorTextDescription: isDark ? "white" : "black",
          colorTextSecondary: isDark ? "white" : "black",
        },

        Tabs: {
          //cardBg: "white",

          // colorBorderSecondary: "white", // Comentado para evitar bordes blancos en dark mode
          itemColor: isDark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.65)",
          itemSelectedColor: isDark ? primaryColorDark : primaryColor,
          itemHoverColor: isDark ? primaryColorDark : primaryColor,
          inkBarColor: isDark ? primaryColorDark : primaryColor,
          titleFontSize: fontSize,
          motionDurationMid: "0.1s",
          motionDurationSlow: "0.1s",
        },
        Spin: {
          colorPrimary: isDark ? primaryColorDark : primaryColor,
        },
        Drawer: {
          motionDurationSlow: "0.2s",
        },
        Form: {
          motionDurationMid: "0.0s",
          motionDurationFast: "0.0s",
        },
        Badge: {
          colorInfo: isDark ? primaryColorDark : primaryColor,
        },
        Notification: {
          colorBgElevated: isDark ? "#1a1d23" : primaryColor,
          colorText: isDark ? "rgba(255,255,255,0.85)" : "white",
          colorTextHeading: isDark ? "white" : "white",
          colorInfo: "rgb(255,255,255)",
          colorSuccess: "white",
          colorIcon: "white",
        },
        Message: {
          colorBgElevated: isDark ? "#1a1d23" : primaryColor,
          colorText: "white",
          colorTextHeading: "white",
          colorInfo: "white",
          colorSuccess: "white",
        },

        Tag: {
          defaultColor: "white",
          colorBorder: isDark ? blue[9] : blue[9],
          defaultBg: isDark ? "dark" : blue[9],
        },
        Menu: {
          activeBarBorderWidth: 0,
          motionDurationSlow: "0.0s",
          motionDurationMid: "0.5s",
          motionDurationFast: "0.0s",
        },
        Table: {
          motionDurationMid: "0.0s",
          motionDurationSlow: "0.5s",
          opacityLoading: 0.75,
          headerBg: isDark ? "#1a1d23" : "#fafafa",
          headerColor: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.85)",
          colorPrimary: isDark ? primaryColorDark : primaryColor,
        },
        Layout: {
          headerBg: isDark ? "#0a0c10" : primaryColor,
          siderBg: isDark ? "#0a0c10" : primaryColor,
          bodyBg: isDark ? "#0a0c10" : "#F0F2F2",
        },

        Button: {
          colorLink: "white",
          motionDurationMid: "0.3s",
          motionDurationSlow: "0.0s",
          colorText: isDark ? "white" : "black",
          colorLinkHover: isDark ? "white" : "#262626",
          colorBgContainerDisabled: isDark ? "#262626" : "white",
          colorTextDisabled: isDark ? "white" : "black",
        },
        Card: {
          headerBg: isDark ? "transparent" : primaryColor,
          extraColor: isDark ? primaryColorDark : "white",
          colorTextHeading: isDark ? "white" : "white",
          borderRadius: 12,
          borderRadiusLG: 16,
          paddingLG: 24,
          headerFontSize: 18,
        },

        Select: {
          borderRadius: borderRadius,
          controlHeight: heights.base,
          optionSelectedBg: isDark ? primaryColorDark : `${primaryColor}20`,
          optionActiveBg: isDark
            ? `${primaryColorDark}40`
            : `${primaryColor}10`,
        },
        Input: {
          borderRadius: borderRadius,
          controlHeight: heights.base,
          hoverBorderColor: primaryColor,
          activeBorderColor: primaryColor,
          motionDurationSlow: "0.0s",
        },
        Button: {
          borderRadius: borderRadius,
          controlHeight: heights.base,
          fontWeight: 500,
        },
        Switch: {
          trackMinWidth: 44,
          trackHeight: 22,
          motionDurationMid: "0.2s",
        },
        Alert: {
          borderRadius: 8,
          paddingContentVertical: 16,
          paddingContentHorizontal: 16,
        },
        Empty: {
          fontSize: 14,
          colorText: isDark ? "#8c8c8c" : "#bfbfbf",
        },
        Badge: {
          textFontSize: 12,
          statusSize: 8,
          colorInfo: isDark ? primaryColorDark : primaryColor,
        },
        Tag: {
          borderRadius: 4,
          fontSizeSM: 11,
          lineHeightSM: 18,
          paddingSM: "2px 8px",
          defaultBg: "transparent",
          defaultColor: "inherit",
          // Controlar altura - CRÍTICO para evitar Tags gigantes
          minHeight: "auto !important",
          height: "auto !important",
          maxHeight: "none",
          // Evitar que los Tags se expandan en flex containers
          marginInlineEnd: 0,
          display: "inline-flex !important",
          alignItems: "center",
          verticalAlign: "middle",
          // Asegurar que el contenido no cause expansión vertical
          boxSizing: "border-box",
          // Prevenir expansión en tablas
          flexShrink: 0,
          flexGrow: 0,
          width: "auto",
          maxWidth: "none",
        },
        Tooltip: {
          // Configuración centralizada de tooltips
          colorBgSpotlight: isDark ? blue[8] : primaryColor,
          colorTextLightSolid: "white",
          borderRadius: 4,
          motionDurationSlow: "0.2s",
          motionDurationMid: "0.15s",
          zIndexPopup: 1060,
        },
        Popconfirm: {
          // Configuración centralizada de popconfirm
          colorBgElevated: isDark ? blue[8] : primaryColor,
          colorText: "white",
          borderRadius: 4,
        },
      },
      typography: {
        fontFamily: "Lato",
      },
    },
  };
};
